import {
  Box,
  Checkbox, CircularProgress,
  Container,
  createStyles,
  Dialog,
  FormControlLabel,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Color } from '@material-ui/lab/Alert';
import axios from 'axios';
import { FieldArray, Form, Formik, useFormikContext } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import * as Yup from 'yup';
import flatten from 'lodash/flatten'
import uniqBy from 'lodash/uniqBy'
import differenceBy from 'lodash/differenceBy'
import { stagingServerUrl } from '../../server/constants';
import { initFalse, ZIP_CODE_CHAR } from '../../utils/constants';
import { stateDropDown } from '../../utils/states';
import { required, requiredSelectBox } from '../../utils/validation';
import { DialogContent, DialogHeader } from '../dialog';
import FormField from '../FindYourStrategy/FormikForms/FormField';
import {
  AutoComplete,
  AutocompleteInputChangeReason,
  Button,
  ErrorCommon,
  FormikValues,
  Label,
  PhoneNumberField,
  SelectField,
  SelectMultipleField,
  TextField
} from '../formikFormInputs';
import { AlertMessage } from '../Common';
import { ImosTable } from './ImosTable';
import { Banner } from './Login';
import { RegistrationConfirmation } from './index';
import { initialValuesPasswordInputs, PasswordInputs, validationPasswordInputs } from './Passwords';
import { consoleLog, noop, pez, va } from '../../utils/commonUtils';
import { useAuth } from '../../lib/useAuth';
import validateEmail from '../../lib/validateEmail';


const requiredKeys = [
  'firstName',
  'lastName',
  'email',
  'mobilePhone',
  'password',
  'confirmPassword',
  'companyName',
  'businessAddress1',
  'city',
  'state',
  'zipCode',
  'workPhone',
  'imos'
];

const useStyles = makeStyles(() =>
  createStyles({
    loginSpace: {
      padding: '1rem 0px 1rem 0px'
    },
    gridStyle: {
      padding: '20px 0px'
    }
  }),
);

const initialImo = {
  imo: {key: null, title: ''},
  state: '',
  phone: '',
  contactFirstName: '',
  contactLastName: '',
  imoEmail: '',
  carriers: [],
  index: null,
  region: ''
}
type ImoForm = typeof initialImo


type DialogImoType = {
  title: string;
  buttonLabel: string;
}

export const dialogImoTypes = {
  add: {
    emptyLabel: "Add new IMO here",
    title: 'Add your IMO here',
    buttonLabel: 'Save'
  },
  edit: {
    title: 'Edit IMO and Carriers',
    buttonLabel: 'Save'
  }
}


export const personalDetailFields = [
  {
    name: 'firstName',
    label: <Label label={'Your First Name'} required={!initFalse}/>,
    type: 'text',
    component: TextField,
    validate: required,
    rowSize: 12
  },
  {
    name: 'lastName',
    label: <Label label={'Your Last Name'} required={!initFalse}/>,
    type: 'text',
    component: TextField,
    validate: required,
    rowSize: 12
  },
  {
    name: 'email',
    label: <Label label={'Your email'} required={!initFalse}/>,
    type: 'email',
    component: TextField,
    validate: required,
    rowSize: 12
  },
  {
    id: 'mobilePhone',
    name: 'mobilePhone',
    label: <Label label={'Mobile Phone'} required={!initFalse}/>,
    component: PhoneNumberField,
    validate: required,
    rowSize: 12
  },
]

export const Register = ({title, subTitle}: any) => {
  const classes = useStyles();
  const router = useRouter()
  const [alert, setAlert] = useState<{ message: string; severity: Color }>({
    message: '',
    severity: 'success'
  });

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    ...initialValuesPasswordInputs,
    companyName: '',
    businessAddress1: '',
    businessAddress2: '',
    city: '',
    state: '',
    zipCode: '',
    workPhone: '',
    mobilePhone: '',
    imos: [],
    noImo: false
  };

  const [loading, setLoading] = React.useState(initFalse);

  const submit = async (values: any, {setSubmitting}: any) => {
    setSubmitting(!initFalse);
    setLoading(!initFalse);

    const excludeKeys = ["noImo", "pageUrl", ...(values.noImo ? ["imos"] : [])];
    const filteredValues: any = Object.entries(values).reduce((acc: any, [key, val]) => {
      if (!excludeKeys.includes(key)) acc[ key ] = val;
      return acc;
    }, {});

    try {
      await axios.post(`${stagingServerUrl}/public-api/agent/request`, filteredValues);
      await router.push('/agent-resources/register-confirmation');
      // @ts-ignore TS1196: Catch clause variable cannot have a type annotation.
    } catch (error: any) {
      setAlert({
        message: (error.response && error.response.data && error.response.data.message) || 'Something went wrong',
        severity: 'error'
      })
    } finally {
      setSubmitting(initFalse);
      setLoading(initFalse);
    }
  }

  const companyFields = [
    {
      name: 'companyName',
      label: <Label label={'Company Name'} required={!initFalse}/>,
      type: 'text',
      component: TextField,
      validate: required,
      rowSize: 12
    },
    {
      name: 'businessAddress1',
      label: <Label label={'Your Business Address 1'} required={!initFalse}/>,
      component: TextField,
      validate: required,
      rowSize: 12
    },
    {
      name: 'businessAddress2',
      label: <Label label={'Your Business Address 2'}/>,
      type: 'text',
      component: TextField,
      className: '',
      rowSize: 12
    },
    {
      name: 'city',
      label: <Label label={'City'} required={!initFalse}/>,
      type: 'text',
      component: TextField,
      validate: required,
      className: '',
      rowSize: 8
    },
    {
      name: 'state',
      label: <Label label={'State'} required={!initFalse}/>,
      type: 'text',
      component: SelectField,
      options: stateDropDown.map(x => ({...x, key: x.value})),
      validate: requiredSelectBox,
      className: '',
      rowSize: 4,
      onSelectChange: noop
    },
    {
      name: 'zipCode',
      label: <Label label={'Zip'} required={!initFalse}/>,
      type: 'text',
      component: TextField,
      validate: required,
      rowSize: 4
    },
    {
      id: 'workPhone',
      name: 'workPhone',
      label: <Label label={'Work Phone'} required={!initFalse}/>,
      component: PhoneNumberField,
      validate: required,
      rowSize: 4
    },
  ];
  const _valSchema = {
    firstName: Yup.string().trim().required().label('First name'),
    lastName: Yup.string().trim().required().label('Last name'),
    email: Yup.string().email().label('Email').required()
      .test('Unique Email','Email already exists', validateEmail),
    ...validationPasswordInputs,
    companyName: Yup.string().trim().required().label('Company name'),
    businessAddress1: Yup.string().trim().required().label('Business address 1'),
    city: Yup.string().trim().required().label('City'),
    state: Yup.string().required().label('State'),
    zipCode: Yup.string().required().label('Zip code').matches(ZIP_CODE_CHAR, 'Invalid zip code'),
    workPhone: Yup.string().required().matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Work phone must be in format (XXX) XXX-XXXX').label('Work phone'),
    mobilePhone: Yup.string().required().matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Mobile phone must be in format (XXX) XXX-XXXX').label('Mobile phone'),
    imos: Yup.array().when('noImo', {
      is: true,
      then: Yup.array(),
      otherwise: Yup.array().required("Please add an IMO and carriers").min(1, 'Please add an IMO and Carriers'),
    }),
    noImo: Yup.boolean()
  };

  return (
    <>
      <Banner title={title} subTitle={subTitle}/>
      <div className="sectionPD">
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} md={10}>
              <Typography color="primary" variant="subtitle1" className="f28" component="strong">
                New Agent Registration
              </Typography>
              <Typography className="textLightGray mt20 mb30">
                Please register. Once you are approved you will receive an email with
                login access. <Link href="/agent-resources">Login here</Link> if you have already registered. For
                questions
                please email <a href="mailto:niw.selling@simplicitygroup.com">niw.selling@simplicitygroup.com</a>.
              </Typography>
              <Formik initialValues={initialValues} onSubmit={submit} validationSchema={Yup.object(_valSchema)}>
                {
                  ({errors, values, touched, handleChange, handleBlur, isValid, dirty, setFieldValue}: FormikValues) => (
                    <Form className="labelShrink">
                      <ScrollToError />
                      <Typography variant="caption" component="strong">
                        Personal Details
                      </Typography>
                      <Grid container spacing={3} className={classes.gridStyle}>
                        <FormInputs
                          fields={personalDetailFields}
                          {...{errors, values, touched, handleChange, handleBlur}} />
                        <PasswordInputs
                          {...{errors, values, touched, handleChange, handleBlur}} />
                      </Grid>
                      <Grid container spacing={4} className={classes.gridStyle}>
                        <Grid item xs={12}>
                          <Typography variant="caption" component="strong" className="floatLeft w100">
                            Your Company/Agency Information
                          </Typography>
                        </Grid>
                        <FormInputs
                          fields={companyFields}
                          {...{errors, values, touched, handleChange, handleBlur}} />
                      </Grid>
                      <AgentImosSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        addLabel={dialogImoTypes.add.title}
                        addDialogImoType={dialogImoTypes.add}
                      />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Button
                          className="mt20 mb40" type="submit" variant="contained" color="primary"
                          disabled={!isValid || !dirty}
                        >
                          Request Approval
                        </Button>
                      </Box>
                    </Form>
                  )
                }
              </Formik>
            </Grid>
          </Grid>
        </Container>
        <RegistrationConfirmation/>
        {
          loading &&
          <Grid alignItems="center" item xs={12} className="lastItemMb20 textCenter">
            <CircularProgress color="inherit"/>
          </Grid>
        }
      </div>
      <AlertMessage
        open={!!alert.message}
        severity={alert.severity}
        setOpen={() => setAlert({message: '', severity: 'success'})}
        message={alert.message}
        autoHide={60000}
      />
    </>
  )
}

export function ScrollToError() {
  const formik = useFormikContext();
  const submitting = formik?.isSubmitting;

  React.useEffect(() => {
    if (!(formik.errors && va(Object.keys(formik.errors)))) {
      return;
    }

    const selectors = requiredKeys.filter(req => Object.keys(formik.errors).includes(req)).map(name => (`[name=${name}]`)).join();
    const el = document.querySelector(selectors);
    (el?.parentElement ?? el)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, [submitting]);

  return null;
}

export const AgentImosSection = ({values, errors, touched, addLabel, addDialogImoType, setFieldValue}: any) => {
  const initialDialog = {type: null, data: initialImo};
  const {user} = useAuth();
  const [imoDialog, setImoDialog] = React.useState<{ type: DialogImoType | null; data: ImoForm & { index: number | null } }>(initialDialog);
  const submitImo = (imo: any, arrayHelpers: any) => {
    if (imoDialog.type === dialogImoTypes.edit) {
      arrayHelpers.replace(imo.index, imo)
    }

    if (imoDialog.type === addDialogImoType) {
      arrayHelpers.push(imo);
    }
  };

  return (
    <>
      <div className="mt30 floatLeft w100">
        <Typography className="wSm-100 floatLeft mt10" variant="subtitle1" component="strong">
          IMO's and Carriers
        </Typography>
        <FormControlLabel
          color="primary"
          className="textPrimary mt15 checkBox"
          label={`I don't have an IMO`}
          disabled={!!(values && values.imos && values.imos.length > 0)}
          control={(
            <Checkbox
              className="cardPadding0 mr10 ml30"
              color="primary"
              name="noImo"
              onChange={() => {
                setFieldValue('noImo', !values.noImo)
              }}
            />
          )}
        />
        {
          !user &&
          <Button
            className="floatRight mb15"
            color="primary"
            variant="contained"
            startIcon={<AddIcon/>}
            onClick={() => setImoDialog({type: addDialogImoType, data: initialImo})}
            disabled={values.noImo}
          >
            {addLabel}
          </Button>
        }
      </div>
      <FieldArray
        name="imos" render={arrayHelpers => (
        <>
          <ImosTable
            label={dialogImoTypes.add.emptyLabel}
            onAddNewImo={() => setImoDialog({type: addDialogImoType, data: initialImo})}
            onDelete={(index) => arrayHelpers.remove(index)}
            rows={values.imos}
            isDisabled={values.noImo}
          />
          {
            typeof errors.imos === 'string' &&
            <ErrorCommon style={{textAlign: 'center'}} errors={errors} name={'imos'} touched={touched}/>
          }
          {
            imoDialog.type &&
            <DialogImo
              imos={values.imos}
              isOnRegisterProcess={addDialogImoType === dialogImoTypes.add}
              handleClose={() => setImoDialog(initialDialog)}
              onSubmit={imo => submitImo(imo, arrayHelpers)}
              initialImo={imoDialog.data}
              type={imoDialog.type}
            />
          }
        </>
      )}
      />
    </>
  )
}

type DialogImoProps = {
  handleClose: () => void;
  onSubmit: (values: any) => void;
  initialImo: ImoForm;
  type: DialogImoType;
  isOnRegisterProcess: boolean;
  imos: any[];
}

export type Carrier = {
  id: number;
  carrierName: string;
}

export const prefixAgentNumber = 'agent_carrier_';

export const getCarriers = async () =>
  axios.get(`${stagingServerUrl}/public-api/carrier`)
    .then(response => response.data)
    .then(body => {
      return body.data;
    });

const getImoById = (imoId: number) =>
  axios.get(`${stagingServerUrl}/public-api/imo/${imoId}`)
    .then(response => response.data)

const DialogImo = ({handleClose, onSubmit, initialImo, type, isOnRegisterProcess, imos}: DialogImoProps) => {
  const [carriers, setCarriers] = React.useState<Carrier[]>([]);
  const [imoList, setImoList] = React.useState<any[]>([initialImo.imo]);
  const [searchingImos, setSearchingImos] = useState(initFalse);
  const [editModeImos, setEditMode] = useState(!!(initialImo.imo.key && initialImo.imo.title));
  const [imoInitialValues, setImoInitialValues] = React.useState<any>(initialImo)
  const [selectOther, setSelectOther] = React.useState<boolean>(false);


  React.useEffect(()=>{
    let ag:any = [];
    carriers.map((carr:any)=>{
      ag[`${prefixAgentNumber}${carr.id}`] = "";
    });

    setImoInitialValues({...imoInitialValues, ...ag});
  }, [carriers])


  React.useEffect(() => {
    (
      async () => {
        await searchImos('');
      }
    )();
  }, []);

  const setInitialCarriers = async () => {
    if (initialImo.imo.key) {
      const userImo = await getImoById(initialImo.imo.key as any)
      const carriers = toCarriersState(userImo)
      setCarriers(carriers);
    } else {
      const carriers = await getCarriers();
      setCarriers(carriers);
    }
  };
  const existingCarriers =
    uniqBy(flatten(imos.map(x => x.carriers)).map(({id, carrierName}) => ({id, carrierName})), 'id');
  const getPossibleCarriers = () => {
    return differenceBy(carriers, existingCarriers, 'id')
  };
  const carrierList = getPossibleCarriers().map(c => {
    return {
      key: c.id,
      value: c,
      name: c.carrierName,
    };
  });
  const searchImos = async (name: string) => {
    setSearchingImos(!initFalse);
    const response = await axios.get(`${stagingServerUrl}/public-api/imo/list`, {
      params: {name}
    })
    const imos = response.data

    const list = imos
      .map((imo: any) => ({key: imo.id, title: imo.groupName}))
      .filter((imo: any) => !existImoInAgentImos(imo));
    list[ list.length ] = {key: 0.1, title: "Other"};
    setImoList(list);
    setSearchingImos(initFalse);
  };
  const toCarriers = (carriers: Carrier[], carrierIds: number[]) =>
    carriers.filter(carrier => carrierIds.includes(carrier.id));
  const submit = (values: any, {setSubmitting}: any) => {
    const {imo: imoInfo, ...rest} = values;

    const agentCarrierNumbers = Object.keys(rest)
      .filter(key => key.startsWith(prefixAgentNumber))
      .map((key) => ({
        carrierId: key.replace(prefixAgentNumber, ''),
        code: rest[ key ]
      }));

    const imo = {
      ...rest,
      carriers: formatCarriers(rest.carriers, agentCarrierNumbers),
      id: imoInfo.key,
      name: selectOther ? values.imoName : imoInfo.title,
      agentCarrierNumbers
    };

    onSubmit(imo)
    setSubmitting(initFalse);
    handleClose();
  }
  const formatCarriers = (carrierIds: number[], agentCarrierNumbers: { carrierId: string, code: string }[]) => {
    const _carriers = toCarriers(carriers, carrierIds);
    return _carriers.map(carrier => {
      const _car: any = agentCarrierNumbers.find(x => +x.carrierId === carrier.id);
      return {
        ...carrier,
        code: _car && _car.code,
        phoneNumber: _car && _car.phoneNumber,
      }
    });
  };

  const toCarriersState = (userImo: any): Carrier[] =>
    userImo.imo.carriers.map((x: any) => ({id: x.carrierId, carrierName: x.carrierName}));
  const setImoDetails = async (imoId: number, setFieldValue: any) => {
    if (imoId === 0.1) {
      setFieldValue('imo', {key: 0, title: 'Other'});
      setSelectOther(true);
      return;
    }
    setEditMode(!initFalse);
    const userImo = await getImoById(imoId);
    setShowStateAndRegionFields(false);
    setFieldValue('phone', userImo.imo.officeNumber);
    setFieldValue('contactFirstName', pez(userImo.firstName));
    setFieldValue('contactLastName', pez(userImo.lastName));
    setFieldValue('imoEmail', pez(userImo.imo.imoEmail));
    setFieldValue('region', pez(userImo.imo.region));
    setFieldValue('state', pez(userImo.imo.state));
    setCarriers(toCarriersState(userImo));
    setFieldValue('imo', {key: userImo.imo.id, title: userImo.imo.groupName});
  }

  const onSelectState = (state: string, setFieldValue: any) => {
    const stateOption = stateDropDown.find(x => x.value === state);
    setFieldValue("region", stateOption && stateOption.region)
  }

  const [showStateAndRegionFields, setShowStateAndRegionFields] = useState(true);
  const imoNameField = [
    {
      component: TextField,
      disabled: false,
      label: <Label label={"Enter IMO Name"} required={!initFalse}/>,
      name: "imoName",
      rowSize: 6
    }
  ]

  const stateAndRegionFields = [
    {
      component: SelectField,
      label: <Label label={'IMO State'} required={!initFalse}/>,
      name: 'state',
      options: stateDropDown.map(x => ({...x, key: x.value})),
      onSelectChange: onSelectState,
      rowSize: 4
    },
    // NIWC-1974 7. Hide region on form for agents when adding carrier:
    // {
    //   component: SelectField,
    //   disabled: true,
    //   label: <Label label={"Region"} required={!initFalse}/>,
    //   name: "region",
    //   onSelectChange: noop,
    //   options: imoRegionDropDown.map(x => ({...x, key: x.value})),
    //   size: 4
    // }
  ];
  const stateAndRegionHiddenFields = [
    {component: 'input', name: 'region', type: 'hidden'},
    {component: 'input', name: 'state', type: 'hidden'},
  ];

  const imoFields = [
    {
      cb: async (text: string, setFieldValue: any, reason: AutocompleteInputChangeReason) => {
        setImoList([initialImo.imo]);
        setShowStateAndRegionFields(true);
        switch (reason) {
          case 'clear':
            setEditMode(initFalse);
            setImoList([initialImo.imo]);
            setSelectOther(false);
            break;
          case 'input':
            if (!searchingImos) {
              await searchImos(text);
            }
            setFieldValue("imo", {key: null, title: text});
            break;
        }
        setFieldValue("imoName", text);
      },
      filterOptions: (options: any) => options,
      clearText: 'Clear Selected IMO & its details',
      component: AutoComplete,
      contentList: imoList,
      freeSolo: !initFalse,
      id: 'imo',
      label: <Label label={'Select your IMO'} required={!initFalse}/>,
      loading: searchingImos,
      name: 'imo',
      onSelectChange: setImoDetails,
      rowSize: 8
    },
    ...(selectOther ? imoNameField : []),
    ...(showStateAndRegionFields ? stateAndRegionFields : []),

    {
      component: TextField,
      disabled: editModeImos,
      label: <Label label={"IMO Contact First Name"} required={!initFalse}/>,
      name: "contactFirstName",
      rowSize: 6
    },
    {
      component: TextField,
      disabled: editModeImos,
      label: <Label label={'IMO Contact Last Name'} required={!initFalse}/>,
      name: 'contactLastName',
      rowSize: 6
    },
    {
      component: PhoneNumberField,
      disabled: editModeImos,
      id: 'phone',
      label: <Label label={'IMO Phone'} required={!initFalse}/>,
      name: 'phone',
      rowSize: 6,
      className: editModeImos ? "PhoneNumber-Disabled":""
    },
    {
      component: TextField,
      disabled: editModeImos,
      label: <Label label={'IMO Email'} required={!initFalse}/>,
      name: 'imoEmail',
      rowSize: 6
    },
    {
      className: 'muiSelect',
      component: SelectMultipleField,
      label: <Label label={'Select Carrier(s)'} required={!initFalse}/>,
      multiple: !initFalse,
      name: 'carriers',
      onSelectChange: (carrierIds: number[]) => { consoleLog(carrierIds) },
      options: carrierList,
      renderValue: (carrierIds: number[]) => {
        const carriersSelected = toCarriers(carriers, carrierIds);
        return carriersSelected.map(x => x.carrierName).join(', ')
      },
      rowSize: 12
    }
  ];
  const description = isOnRegisterProcess ?
    'Add an IMO and carrier below. IMO\'s will be verified during your registration verification process by NIW.' :
    type === dialogImoTypes.edit ?
      'Add your IMO below and any authorized carriers for the IMO you enter' :
      'Request an IMO and carriers below. Your request will be reviewed by NIW to verify your IMO details.';

  const _valEmail = Yup.string().email().required().label('IMO Email');
  const _validationSchema = {
    imoName: Yup.string().when([], {
      is: () => selectOther,
      then: Yup.string().required("IMO Name is required"),
      otherwise: Yup.string().notRequired(),
    }),
    imo: Yup.object({key: Yup.number().nullable(), title: Yup.string()})
      .nullable()
      .test(
        'imoRequired',
        'IMO is mandatory',
        (value: any) => value && (typeof value === 'string' || value.title))
      .test(
        'imoNotExist',
        "Already an IMO of this Agent",
        (value: any) => value && (value.title === initialImo.imo.title || !existImoInAgentImos(value)),
      ),
    phone: Yup.string().required().label('Phone'),
    contactFirstName: Yup.string().required().label('First Name'),
    contactLastName: Yup.string().required().label('Last Name'),
    imoEmail: editModeImos ? _valEmail : _valEmail.test('Unique Email','Email already exists', validateEmail),
    carriers: Yup.array(Yup.string().required()).required().min(1).label('Carriers')
  };
  const existImoInAgentImos = (imo: any) => {
    return imos.map(x => x.name).includes(imo.title)
  }

  React.useEffect(() => {
    setInitialCarriers()
      .then();
  }, [])

  return (
    <Dialog fullWidth={!initFalse} open={!initFalse} maxWidth="md">
      <DialogHeader onClose={handleClose}>
        {type.title}
      </DialogHeader>
      <Formik initialValues={imoInitialValues} onSubmit={submit} validationSchema={Yup.object(_validationSchema)} enableReinitialize={true}>
        {
          ({errors, values, touched, handleChange, handleBlur}: FormikValues) => (
            <Form>
              <DialogContent dividers>
                <Typography variant="body1" className="mb15">
                  {description}
                </Typography>
                <Grid container spacing={3}>
                  <FormInputs fields={imoFields} {...{errors, values, touched, handleChange, handleBlur}} />
                  {
                    !showStateAndRegionFields &&
                    stateAndRegionHiddenFields.map(({component, ...inpProps}) => React.createElement(component, inpProps))
                  }

                  <Grid item xs={12}>
                    <Button size="small" className="floatLeft mt20 mb20" variant="contained" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      className="floatRight mt20 mb20"
                      color="primary"
                      label={type.buttonLabel}
                      size="small"
                      type="submit"
                      variant="contained"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
            </Form>
          )
        }
      </Formik>
    </Dialog>
  )
}

export const FormInputs = ({fields, values, errors, touched, handleChange, handleBlur}: any) => {
  return (
    <>
      {
        fields.map((field: any, index: any) => (
          <FormField
            key={index}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            {...field} />
        ))
      }
    </>
  )
}
